import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlanAction } from "@/components/admin/UserPlanAction";

const FORBIDDEN_STATUS = 403;

const accessSourceLabel = (row) => {
  if (row.role === "admin") return "Admin account";
  if (row.plan !== "master") return "—";
  if (row.access_source === "granted") return `Granted by ${row.granted_by || "owner"}`;
  return "Paid";
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const qRef = useRef("");
  const [plan, setPlan] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, master: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", {
        params: { q: qRef.current || undefined, plan: plan || undefined },
      });
      setRows(data.items);
      setCounts(data.counts);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === FORBIDDEN_STATUS) setDenied(true);
      else toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [plan]);

  useEffect(() => {
    load();
  }, [load]);

  const runSearch = () => {
    qRef.current = q;
    load();
  };

  const setUserPlan = async (row, next) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/admin/users/${row.id}/plan`, { plan: next });
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      toast.success(next === "master" ? "MasterPlan granted" : "Access revoked");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  if (denied)
    return (
      <div data-testid="users-access-denied" className="border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        Only the owner account can manage user access. You are signed in as {user?.email}.
      </div>
    );

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <PageHead
        title="Users & access"
        subtitle={`${counts.master} of ${counts.total} accounts have full MasterPlan access. Grants are permanent until you revoke them.`}
      />

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-white p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Search</span>
          <div className="flex">
            <input
              data-testid="user-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Name, email or phone"
              className="h-9 w-72 border border-slate-300 px-2 text-sm"
            />
            <button
              data-testid="user-search-btn"
              onClick={runSearch}
              className="flex h-9 items-center gap-1 bg-[#1A365D] px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
            >
              <Search className="h-3.5 w-3.5" /> Find
            </button>
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Plan</span>
          <select
            data-testid="user-plan-filter"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="h-9 border border-slate-300 px-2 text-sm"
          >
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="master">MasterPlan</option>
          </select>
        </label>
      </div>

      <div className="overflow-auto border border-slate-200 bg-white">
        <table className="data-table" data-testid="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Plan</th>
              <th>How</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-slate-500" data-testid="users-loading">
                  Loading accounts…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-slate-500">
                  No accounts match this search.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`user-row-${r.id}`}>
                <td className="max-w-[280px] truncate">
                  <span className="font-medium">{r.name || "—"}</span>
                  <span className="block text-xs text-slate-500">{r.email || "no email"}</span>
                </td>
                <td className="font-mono-num text-xs">{r.phone || "—"}</td>
                <td className="text-xs uppercase tracking-wider text-slate-500">{r.role}</td>
                <td>
                  <span
                    data-testid={`user-plan-${r.id}`}
                    className={`px-2 py-0.5 text-xs font-semibold ${
                      r.plan === "master" ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {r.plan === "master" ? "MasterPlan" : "Free"}
                  </span>
                </td>
                <td className="text-xs text-slate-500">
                  {accessSourceLabel(r)}
                </td>
                <td>
                  <UserPlanAction row={r} busyId={busyId} onPlanChange={setUserPlan} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Team members must sign in once (WhatsApp or email OTP) before they appear here.
      </p>
    </div>
  );
}
